import cloneDeep from 'lodash/cloneDeep';

import {
  Component,
  EventEmitter,
  Output,
  OnInit,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
  SimpleChanges,
} from '@angular/core';

import { MapComponent } from '@app/map/map.component';

import { gmapsLoadPromise } from '@app/lib/google-maps';
import { createEvent, formatNumber, kmToMiles, milesToKm } from '@app/lib/utils';
import {
  polylinesPromise,
  polygonPromise,
  DRAWING_DEFAULT,
  GEOFENCE_CLASSIC_THEME_OPACITY,
  GEOFENCE_HOVER_OPACITY,
  GEOFENCE_STYLE_OPTIONS,
  POLYLINE_STYLE_OPTIONS,
  markerIconsPromise,
  centroidMarkerIconsPromise,
  ICentroidIconTypes,
  FLEET_VIEW_MAP_ICON_THEME,
  IDrawingThemeIcons,
  GEOFENCE_MARKER_Z_INDEX,
  MARKER_ICON_Z_INDEX,
  EXISTING_GEOFENCE_STYLE_OPTIONS,
  circlePromise,
} from '@app/map/geofence-drawing-config';
import {
  findOverlappingLines,
  validateMinimumGeofenceSize,
  validateMaximumGeofenceSize,
  IGeofenceError,
  IGeofenceChangeData,
  findOverlappingMarkers,
  getDistance,
  MINIMUM_MARKER_DISTANCE,
  findMarkerPolylineOverlapping,
  convertLatlngToPoint,
  DEFAULT_GEOFENCE_RADIUS_METERS,
  getCentroidForGeofence,
  MINIMUM_FENCE_AREA_SQ_METERS,
  MINIMUM_FENCE_AREA_SQ_FEET,
  WARNING_THRESHOLD_FENCE_AREA_SQ_METERS,
  MAXIMUM_FENCE_AREA_SQ_KM,
  MAXIMUM_FENCE_AREA_SQ_MILE,
  WARNING_THRESHOLD_FENCE_AREA_SQ_FEET,
  validateAddressInsideGeofence,
  DEFAULT_CIRCLE_GEOFENCE_RADIUS_METERS,
  MINIMUM_CIRCLE_RADIUS_METERS,
  MAXIMUM_CIRCLE_RADIUS_METERS,
  convertCircleToPath,
  ICircleData,
  MINIMUM_CIRCLE_RADIUS_MILE,
  MAXIMUM_CIRCLE_RADIUS_MILE,
} from '@app/map/utils';
import { MetricUnitsPipe } from '@app/pipes/metric-units';

import { IGeofencePoint, IGeofence } from '@app/admin/geofences/form.service';

import { MAP_TYPES } from '@app/fleet-view-map/fleet-view-map-controls.component';

import 'style-loader!./geofence-drawing-tool.component.deep.scss';
import { INewCentroidIconTypes } from '@app/map/geofence-drawing-config';
import { FleetViewFeatureFlagService } from '@services/feature-flags/fleet-view';
import { newCentroidMarkerIconsPromise } from '@app/map/geofence-drawing-config';

const MAX_GEOFENCE_POINTS: number = 32;
const ERROR_MSG_DURATION: number = 4000; // 4000 milliseconds ≈ 4 seconds
const POLYGON_INSERT_MARKER: string = 'insert_at';
const GEOFENCE_SELECTION_ZOOM_OUT_OFFSET = 1;
enum ADDRESS_PIN_TYPES {
  DEFAULT = 'default',
  ERROR = 'error',
}

export const GEOFENCE_ADDRESS_ERROR_STATE = 'addressOutsideFence';
const MAX_FORM_RADIUS_CHAR: number = 3;

interface IGeofenceRevertingErrorStates {
  overlapped: IGeofenceError;
  tooManyPoints: IGeofenceError;
}

interface IGeofenceNonRevertingErrorStates {
  tooLarge: IGeofenceError;
  tooSmall: IGeofenceError;
  radiusTooLarge: IGeofenceError;
  radiusTooSmall: IGeofenceError;
  addressOutsideFence: IGeofenceError;
}

interface IGeofenceWarningState {
  small: IGeofenceError;
}

enum TOOL {
  POLYGON = 'POLYGON',
  CIRCLE = 'CIRCLE',
  DRAWING = 'DRAWING',
}

type GoogleLatLng = google.maps.LatLng;
type GoogleShape = google.maps.Polygon | google.maps.Circle;

@Component({
  selector: 'geofence-drawing-tool',
  templateUrl: './geofence-drawing-tool.component.html',
  styleUrls: ['./geofence-drawing-tool.component.scss'],
})
export class GeofenceDrawingToolComponent implements OnInit, OnDestroy {
  public TOOL = TOOL;

  @Output() public drawingStarted: EventEmitter<any> = new EventEmitter();
  @Output() public drawingCanceled: EventEmitter<any> = new EventEmitter();
  @Output() public polygonChanged: EventEmitter<IGeofenceChangeData> = new EventEmitter();
  @Output() public drawingErrorChanged: EventEmitter<any> = new EventEmitter();
  @Output() public drawingWarningChanged: EventEmitter<any> = new EventEmitter();
  @Output() public geofenceCenterChanged: EventEmitter<google.maps.LatLng> = new EventEmitter<google.maps.LatLng>();
  @Input() public map: MapComponent;
  @Input() public isDrawingThroughFV: boolean = false;
  @Input() public isMetricUnitsSelected: boolean = false;
  @Input() public isEditMode: boolean = true;
  @Input() public selectedMapTypeTheme: FLEET_VIEW_MAP_ICON_THEME = FLEET_VIEW_MAP_ICON_THEME.CLASSIC;
  @Input() public lockedGeofenceAddress: GoogleLatLng;

  public revertingErrorState: IGeofenceError;
  public warningState: IGeofenceError;
  public nonRevertingErrorState: IGeofenceError;
  public selectedTool: TOOL = TOOL.POLYGON;
  public circle: google.maps.Circle;
  public enableDrawingControls: boolean = false;
  public showDrawingBanner: boolean = false;
  public geofenceTooltipText: string = '';
  public squareCenter: google.maps.LatLng;
  /**
   * A copy of the circle's radius in miles or km, based on the user's settings, and formatted to the correct
   * number of significant digits.
   */
  public formRadius: string = this.convertRadiusMetersToMilesOrKm(DEFAULT_CIRCLE_GEOFENCE_RADIUS_METERS).toString();

  public get metricUnit(): string {
    return this.metricUnitsPipe.transform(this.isMetricUnitsSelected, 0, 'distance');
  }

  public get isPolygonInMap(): boolean {
    return this.polygon && this.polygon.getMap() === this.mapRef;
  }

  public get isCircleInMap(): boolean {
    return this.circle && this.circle.getMap() === this.mapRef;
  }

  public get isShapeInMap(): boolean {
    return this.isPolygonInMap || this.isCircleInMap;
  }

  public get cancelButtonText(): string {
    switch (this.selectedTool) {
    case TOOL.POLYGON:
    case TOOL.CIRCLE:
      return $localize`Clear`;
    case TOOL.DRAWING:
      return $localize`Reset`;
    }
  }

  public get shouldDisableUndoAndReset(): boolean {
    if (this.revertingErrorState) return true;
    switch (this.selectedTool) {
    case TOOL.POLYGON:
      return !this.polygonGeofenceEdits.length;
    case TOOL.CIRCLE:
      return !this.circleEdits.length;
    case TOOL.DRAWING:
      return !this.markers.length;
    }
  }

  public get shouldDisableRedo(): boolean {
    if (this.revertingErrorState) return true;
    switch (this.selectedTool) {
    case TOOL.POLYGON:
      return !this.polygonGeofenceRedos.length;
    case TOOL.CIRCLE:
      return !this.circleRedos.length;
    case TOOL.DRAWING:
      return !(
        this.markersBackup?.length > 0 || (this.isPolygonDestroyed && this.markers?.length > 0)
      );
    }
  }

  public get defaultCircleEditData(): ICircleData {
    return this.editingCircleGeofence || {
      radius: DEFAULT_CIRCLE_GEOFENCE_RADIUS_METERS,
      center: this.squareCenter,
    };
  }

  public get hasCircleRadiusInputError(): boolean {
    return [
      this.nonRevertingErrorStates.radiusTooLarge,
      this.nonRevertingErrorStates.radiusTooSmall,
    ].includes(this.nonRevertingErrorState);
  }

  @Input() private selectedMapType: MAP_TYPES;
  private el: HTMLElement;
  private gmaps;
  private mapRef;
  private markerIcons: any;
  private centroidMarkerIcons: ICentroidIconTypes;
  private polylines: any;
  private markers: google.maps.Marker[] = [];
  private markersBackup: google.maps.Marker[] = [];
  private polygon: google.maps.Polygon;
  private mapClickedEvent: google.maps.event;
  private markerDragStartAt: google.maps.LatLng;
  private isPolygonDestroyed: boolean = false;
  private draggedMarker: google.maps.Marker;
  private isDrawingModeEnabled: boolean = false;
  private clickedPoint: google.maps.LatLng;
  private mapBoundsChangedEvent: google.maps.event;
  private existingGeofences: google.maps.Polygon[] = [];
  private errorMarker: google.maps.Marker;
  private polygonDragStartAt: google.maps.LatLng[];
  private cursorDot: HTMLElement;
  private centroidForGeofence: google.maps.Marker;
  private polygonInsertAtEvent: google.maps.MapsEventListener;
  private selectedMapThemeDrawingMarkers: IDrawingThemeIcons;
  private mapDragStartEvent: google.maps.event;
  private mapDragEndEvent: google.maps.event;
  private polygonGeofenceEdits: google.maps.LatLng[][] = [];
  private polygonGeofenceRedos: google.maps.LatLng[][] = [];
  private circleEdits: ICircleData[] = [];
  private circleRedos: ICircleData[] = [];
  private defaultPolygonGeofence: google.maps.LatLng[] = [];
  /** The data for the circular geofence which is currently being edited (if any). */
  private editingCircleGeofence: ICircleData = null;
  private drawingToolCache: google.maps.LatLng[] = [];
  private skipRadiusRoundingOnce: boolean = false;
  private formRadiusInputRegExp: RegExp = null;
  private prevFormRadius: string = this.formRadius;
  private nonRevertingErrorStates: IGeofenceNonRevertingErrorStates = {
    tooSmall: {
      type: 'tooSmall',
      message: '',
      note: $localize`Our recommendation is based on typical GPS accuracy.`,
      shortMessage: $localize`Geofence size is too small`,
    },
    tooLarge: {
      type: 'tooLarge',
      message: '',
      shortMessage: $localize`Geofence size is too big`,
    },
    radiusTooSmall: {
      type: 'radiusTooSmall',
      message: '',
      note: $localize`Our recommendation is based on typical GPS accuracy.`,
      shortMessage: $localize`Geofence size is too small`,
    },
    radiusTooLarge: {
      type: 'radiusTooLarge',
      message: '',
      shortMessage: $localize`Geofence size is too big`,
    },
    [GEOFENCE_ADDRESS_ERROR_STATE]: {
      type: 'addressOutsideFence',
      message: '',
    },
  };
  private revertingErrorStates: IGeofenceRevertingErrorStates = {
    overlapped: {
      type: 'overlapped',
      message: '',
      shortMessage: $localize`Geofence path is overlapping`,
    },
    tooManyPoints: {
      type: 'tooManyPoints',
      message: '',
      shortMessage: $localize`Geofence has too many points`,
    },
  };
  private warningStates: IGeofenceWarningState = {
    small: {
      type: 'small',
      message: '',
    },
  };
  private readonly mouseClickedEvent: google.maps.MapMouseEvent = {
    stop() { return; },
    latLng: new google.maps.LatLng(0, 0),
    domEvent: new MouseEvent('click'),
  };
  private newCentroidMarkerIcons: INewCentroidIconTypes;
  private areNewMarkersEnabled: boolean = false;
  @ViewChild('invisibleOverlay', { static: false }) private invisibleOverlay: ElementRef;
  @ViewChild('geofencePopup', { static: true }) private geofencePopup: ElementRef;

  constructor(
    private elRef: ElementRef,
    private metricUnitsPipe: MetricUnitsPipe,
    fleetViewFeatureFlag: FleetViewFeatureFlagService,
  ) {
    this.el = this.elRef.nativeElement;
    gmapsLoadPromise.then((gmaps: any) => {
      this.gmaps = gmaps;
    });
    this.formRadiusInputRegExp = this.getFormRadiusInputRegExp();
    this.areNewMarkersEnabled = fleetViewFeatureFlag.isMarkersMapLayersAndLegendsEnabled();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedMapTypeTheme &&
        (this.polygon || this.circle) &&
        changes.selectedMapTypeTheme.previousValue !== this.selectedMapTypeTheme
      ) {
      this.setDrawingTheme();
    }

    if (
      (this.polygon || this.circle) &&
      changes['lockedGeofenceAddress'] && changes['lockedGeofenceAddress'].currentValue !== undefined
      ) {
      this.lockedGeofenceAddress = changes['lockedGeofenceAddress'].currentValue;
      this.updateGeofenceAddressPin();
      if (this.isShapeInMap) {
        this.validateNonRevertingErrors();
        this.polygonChanged.emit(this.getGeofenceChangeData(false));
      }
    }
  }

  public ngOnInit(): void {
    this.cursorDot = this.el.querySelector('#cursor-dot') as HTMLElement;
    if (this.isDrawingThroughFV) {
      this.mapRef = this.map;
      this.initMapFunctionsAndEvents();
    } else {
      this.map.createMapPromise.then((map: google.maps.Map) => {
        this.mapRef = this.map.map;
        this.initMapFunctionsAndEvents();
      });
    }
    this.initializeErrorAndWarningMessages();
  }

  public ngOnDestroy(): void {
    this.polylines.default.setOptions({
      map: null,
      path: [],
    });

    this.destroyPolygonFence();

    this.markers.forEach(marker => marker.setMap(null));
    this.markers = [];
    this.disableDrawing();
    this.gmaps.event.removeListener(this.mapBoundsChangedEvent);
    this.gmaps.event.removeListener(this.mapDragStartEvent);
    this.gmaps.event.removeListener(this.mapDragEndEvent);
  }

  public setShapeColor(color: string): void {
    if (this.polygon) this.polygon.setOptions({ fillColor: color });
    if (this.circle) this.circle.setOptions({ fillColor: color });
  }

  public initUpdateGeofence(geofenceData: IGeofenceChangeData, color: string = null): void {
    Promise.all([gmapsLoadPromise, polylinesPromise, polygonPromise, circlePromise])
      .then(() => {
        const isCircle: boolean = !!geofenceData.center && !!geofenceData.radius;

        let center: google.maps.LatLng;
        if (isCircle) {
          this.selectedTool = TOOL.CIRCLE;
          this.initUpdateCircularGeofence({ center: geofenceData.center, radius: geofenceData.radius });
          center = geofenceData.center;
          this.defaultPolygonGeofence = this.getDefaultPolygonPath(center);
        } else {
          this.selectedTool = TOOL.POLYGON;
          this.initUpdatePolygonGeofence(geofenceData.polygonPath);
          center = getCentroidForGeofence(geofenceData.polygonPath);
        }
        if (color) this.setShapeColor(color);
        this.squareCenter = center;
        this.setGeofenceInView();
      });
  }

  private initUpdatePolygonGeofence(points: google.maps.LatLng[]): void {
    const bounds: google.maps.LatLngBounds = new google.maps.LatLngBounds();
    points.forEach(p => {
      bounds.extend(p);
      this.drawMarker(p);
    });
    if (this.isDrawingThroughFV) this.defaultPolygonGeofence = points;
    google.maps.event.trigger(this.markers[0], 'click', {
      latLng: points[0],
    }, false);
    this.mapRef.fitBounds(bounds);
    this.startDrawing();
  }

  private initUpdateCircularGeofence(circleData: ICircleData): void {
    if (this.isDrawingThroughFV) this.editingCircleGeofence = circleData;
    this.updateCircle(circleData.center, circleData.radius);
  }

  public drawGeofences(geofences: IGeofence[]): void {
    this.destroyExistingGeofences();
    geofences.forEach((geofence: IGeofence) => {
      const points: google.maps.LatLng[] = geofence.locationPoints.map((point: IGeofencePoint) => {
        return new google.maps.LatLng(point.lat, point.lon);
      });
      this.existingGeofences.push(this.drawExistingGeofence(points, geofence));
    });
  }

  public selectTool(nextTool: TOOL): void {
    if (this.selectedTool === nextTool) return;
    switch (this.selectedTool) {
    case TOOL.POLYGON:
    case TOOL.CIRCLE:
      break;
    case TOOL.DRAWING:
      this.teardownDrawingTool();
      break;
    }

    this.resetGeofenceErrorStates();
    this.resetWarningState();
    this.removeMapObjects();

    this.selectedTool = nextTool;

    switch (nextTool) {
    case TOOL.POLYGON:
      this.setupPolygonTool();
      break;
    case TOOL.CIRCLE:
      this.setupCircleTool();
      break;
    case TOOL.DRAWING:
      this.setupDrawingTool();
      break;
    }
  }

  public drawDefaultPolygonGeofence(centerPoint: google.maps.LatLng): void {
    this.squareCenter = centerPoint;
    const squarePolygonPath = this.getDefaultPolygonPath(centerPoint);
    Promise.all([gmapsLoadPromise, polylinesPromise, polygonPromise, circlePromise])
    .then(() => {
      squarePolygonPath.forEach((point: google.maps.LatLng) => {
        this.drawMarker(point);
      });
      this.drawPolygon(false);
      this.selectedTool = TOOL.POLYGON;
      this.defaultPolygonGeofence = cloneDeep(squarePolygonPath);
    });
  }

  private getDefaultPolygonPath(centerPoint: google.maps.LatLng): google.maps.LatLng[] {
    const circle: google.maps.Circle = new google.maps.Circle({radius: DEFAULT_GEOFENCE_RADIUS_METERS, center: centerPoint});
    const squarePolygonPath: google.maps.LatLng[] = [];
    const squareBounds = circle.getBounds();
    const NECornerLatLngs: google.maps.LatLng = squareBounds.getNorthEast();
    const SWCornerLatLngs: google.maps.LatLng = squareBounds.getSouthWest();
    const NWCornerLatLngs: google.maps.LatLng = new google.maps.LatLng(NECornerLatLngs.lat(), SWCornerLatLngs.lng());
    const SECornerLatLngs: google.maps.LatLng = new google.maps.LatLng(SWCornerLatLngs.lat(), NECornerLatLngs.lng());
    squarePolygonPath.push(NWCornerLatLngs, NECornerLatLngs, SECornerLatLngs, SWCornerLatLngs);
    return squarePolygonPath;
  }

  public drawExistingGeofence(points: google.maps.LatLng[], geofence: IGeofence): google.maps.Polygon {
    const existingGeofence = new google.maps.Polygon({
      strokeColor: EXISTING_GEOFENCE_STYLE_OPTIONS[this.selectedMapTypeTheme].default.strokeColor,
      strokeWeight: EXISTING_GEOFENCE_STYLE_OPTIONS[this.selectedMapTypeTheme].default.strokeWeight,
      strokeOpacity: EXISTING_GEOFENCE_STYLE_OPTIONS[this.selectedMapTypeTheme].default.strokeOpacity,
      fillColor: EXISTING_GEOFENCE_STYLE_OPTIONS[this.selectedMapTypeTheme].default.fillColor,
      fillOpacity: EXISTING_GEOFENCE_STYLE_OPTIONS[this.selectedMapTypeTheme].default.fillOpacity,
      map: this.mapRef,
      zIndex: 1,
      paths: points,
      draggable: false,
      clickable: false,
    });

    existingGeofence.addListener('click', this.onMapClicked.bind(this));
    existingGeofence.addListener('mouseover', (e: google.maps.MapMouseEvent) => {
      this.geofenceTooltipText = geofence.name;
      if (!this.isDrawingThroughFV) {
        const geofencePopupEl: HTMLElement = this.geofencePopup.nativeElement as HTMLElement;
        const popupPosition: google.maps.Point = convertLatlngToPoint(e.latLng, this.mapRef);
        geofencePopupEl.style.left = `${popupPosition.x}px`;
        geofencePopupEl.style.top = `${popupPosition.y}px`;
        const mouseoverEvent: Event = createEvent('mouseover');
        setTimeout(() => {
          geofencePopupEl.dispatchEvent(mouseoverEvent);
        }, 0);
      }
    });

    existingGeofence.addListener('mouseout', () => {
      const geofencePopupEl: HTMLElement = this.geofencePopup.nativeElement as HTMLElement;
      const mouseoutEvent: Event = createEvent('mouseout');
      geofencePopupEl.dispatchEvent(mouseoutEvent);
      this.geofenceTooltipText = '';
    });
    return existingGeofence;
  }

  public destroyExistingGeofences(): void {
    this.existingGeofences.forEach((geofence: google.maps.Polygon) => {
      geofence.setMap(null);
    });

    this.existingGeofences = [];
  }

  public startDrawing(): void {
    if (!this.isPolygonInMap) {
      this.drawingStarted.emit();
      if (this.selectedTool !== TOOL.DRAWING) {
        this.invisibleOverlay.nativeElement.classList.remove('invisible');
        this.selectedTool = TOOL.DRAWING;
      }
    }
  }

  public undo(): void {
    if (!this.isDrawingThroughFV) return this.undoDrawing();
    switch (this.selectedTool) {
    case TOOL.POLYGON:
      return this.undoPolygon();
    case TOOL.CIRCLE:
      return this.undoCircle();
    case TOOL.DRAWING:
      return this.undoDrawing();
    }
  }

  public redo(): void {
    if (!this.isDrawingThroughFV) return this.redoDrawing();
    switch (this.selectedTool) {
    case TOOL.POLYGON:
      return this.redoPolygon();
    case TOOL.CIRCLE:
      return this.redoCircle();
    case TOOL.DRAWING:
      return this.redoDrawing();
    }
  }

  public cancel(): void {
    this.resetGeofenceErrorStates();
    this.resetWarningState();
    if (this.markers?.length) {
      this.markers.forEach(marker => {
        marker.setMap(null);
      });
      this.markers = [];
      this.markersBackup = [];
      this.enableDrawingControls = false;
      this.updateMarkers();
      if (!this.isEditMode) this.setShapeColor(DRAWING_DEFAULT);
      this.polygon.setMap(null);
      this.circle.setMap(null);
      this.polygonDragStartAt = null;
      this.polygonChanged.emit(null);
    }

    if (!this.isDrawingThroughFV) {
      this.cancelDrawing();
    } else {
      switch (this.selectedTool) {
      case TOOL.POLYGON:
        this.cancelPolygon();
        break;
      case TOOL.CIRCLE:
        this.cancelCircle();
        break;
      case TOOL.DRAWING:
        this.cancelDrawing();
      }
    }

    this.updateGeofenceAddressPin();
    this.drawingCanceled.emit();
  }

  public onUpdateFormRadius(radius: string, circleRadiusInput: HTMLInputElement): void {
    // Validate input.
    const isValid: boolean = !!radius.match(this.formRadiusInputRegExp);
    if (isValid) {
      this.prevFormRadius = this.formRadius;
      this.formRadius = radius;
    }
    else this.formRadius = this.prevFormRadius;
    circleRadiusInput.value = this.formRadius;

    if (isValid) {
      let floatRadius: number = parseFloat(this.formRadius);
      floatRadius = isNaN(floatRadius) ? 0 : floatRadius;
      const radiusMeters: number = this.isMetricUnitsSelected ? floatRadius * 1000 : milesToKm(floatRadius * 1000);
      this.updateCircle(this.circle.getCenter(), radiusMeters);
    }
  }

  public enableDrawing(): void {
    this.mapRef.setOptions({
      draggableCursor: 'default',
    });
    this.hideCursorDot(false);
    if (!this.isDrawingModeEnabled) this.registerMapEvents();
    this.isDrawingModeEnabled = true;
  }

  public getErrorsInDrawing(): string {
    if (this.revertingErrorState) {
      return this.revertingErrorState.message;
    }
    if (this.nonRevertingErrorState) {
      return this.nonRevertingErrorState.message;
    }
  }

  private setupPolygonTool(): void {
    const lastDrawnPolygon: google.maps.LatLng[] = this.polygonGeofenceEdits.length ?
      this.polygonGeofenceEdits[this.polygonGeofenceEdits.length - 1] : this.defaultPolygonGeofence;
    this.drawBackupPolygon(lastDrawnPolygon);
  }

  public setupCircleTool(): void {
    const { radius, center } = this.circleEdits.length ?
      this.circleEdits[this.circleEdits.length - 1] : this.defaultCircleEditData;

    this.updateCircle(center, radius);
  }

  public setupDrawingTool(): void {
    this.enableDrawing();
    this.startDrawing();
    if (this.drawingToolCache.length > 0) {
      this.drawBackupPolygon(this.drawingToolCache);
    }
  }

  private teardownDrawingTool(): void {
    if (this.isPolygonInMap) {
      this.drawingToolCache = cloneDeep(this.getMarkerPositions());
    }
  }

  private undoPolygon(): void {
    if (this.polygonGeofenceEdits.length > 0) {
      const latLngs: google.maps.LatLng[] = this.polygonGeofenceEdits.pop();
      this.polygonGeofenceRedos.push(latLngs);
      const lastAction: google.maps.LatLng[] = this.polygonGeofenceEdits.length ?
        this.polygonGeofenceEdits[this.polygonGeofenceEdits.length - 1] : this.defaultPolygonGeofence
      ;
      this.removeMapObjects();
      this.drawBackupPolygon(lastAction);
    }
  }

  private undoCircle(): void {
    if (!this.circleEdits.length) return;

    this.circleRedos.push(this.circleEdits.pop());
    const { radius, center } =
      this.circleEdits.length ? this.circleEdits[this.circleEdits.length - 1] : this.defaultCircleEditData;

    this.removeMapObjects();
    this.updateCircle(center, radius);
  }

  private undoDrawing(): void {
    this.enableDrawing();
    if (this.isPolygonInMap) {
      this.polygon.setMap(null);
      this.isPolygonDestroyed = true;
      this.polygonChanged.emit(null);
      this.polygonDragStartAt = null;
      this.updateMarkers();
      this.updateGeofenceAddressPin();
      this.resetNonRevertingErrors();
      this.resetWarningState();
    } else if (this.markers?.length) {
      this.removeLastMarker(true);
    }
    this.drawingToolCache = [];
  }

  private redoPolygon(): void {
    if (this.polygonGeofenceRedos.length > 0) {
      const lastRedoAction: google.maps.LatLng[] = this.polygonGeofenceRedos.pop();
      this.polygonGeofenceEdits.push(lastRedoAction);
      this.removeMapObjects();
      this.drawBackupPolygon(lastRedoAction);
    }
  }

  private redoCircle(): void {
    if (!this.circleRedos.length) return;

    this.circleEdits.push(this.circleRedos.pop());
    const { radius, center } = this.circleEdits[this.circleEdits.length - 1];

    this.removeMapObjects();
    this.updateCircle(center, radius);
  }

  private redoDrawing(): void {
    if (this.markersBackup && this.markersBackup.length > 0) {
      this.addLastMarker();
      return;
    }

    if (this.isPolygonDestroyed) {
      this.removeInsertAtListener();
      this.polygon.setMap(this.mapRef);
      this.updatePolygon(false);
      this.isPolygonDestroyed = false;
    }
  }

  private cancelPolygon(): void {
    this.polygonGeofenceEdits = [];
    this.polygonGeofenceRedos = [];
    const addressLatLng: google.maps.LatLng = this.lockedGeofenceAddress || this.squareCenter;
    if (this.isEditMode) {
      this.drawBackupPolygon(this.defaultPolygonGeofence);
    } else this.drawDefaultPolygonGeofence(addressLatLng);
  }

  private cancelCircle(): void {
    this.circleEdits = [];
    this.circleRedos = [];
    this.updateCircle(this.defaultCircleEditData.center, this.defaultCircleEditData.radius);
  }

  private cancelDrawing(): void {
    this.drawingToolCache = [];
    this.enableDrawing();
  }

  /**
   * Convert the radius to either km/mi and remove least significant digits per the maximum number of characters.
   * @param radiusMeters The original radius.
   * @returns The radius in km/mi.
   */
  private convertRadiusMetersToMilesOrKm(radiusMeters: number): number {
    const milesOrKm: number = this.isMetricUnitsSelected ? radiusMeters / 1000 : kmToMiles(radiusMeters / 1000);
    const wholePortion: number = Math.round(milesOrKm);
    const decimalDigits: number = Math.max(0, MAX_FORM_RADIUS_CHAR - wholePortion.toString().length);
    return parseFloat(milesOrKm.toFixed(decimalDigits));
  }

  /**
   * If the radius is above 1 km/mi after conversion to km/mi, convert it to a whole number in km/mi and then return
   * the number of meters.
   * @param radiusMeters The original radius.
   * @returns The conditionally-rounded radius.
   */
  private conditionalRoundRadiusMeters(radiusMeters: number): number {
    let milesOrKm: number = this.convertRadiusMetersToMilesOrKm(radiusMeters);
    if (milesOrKm > 1) {
      milesOrKm = Math.round(milesOrKm);
    }
    return this.isMetricUnitsSelected ? milesOrKm * 1000 : milesToKm(milesOrKm) * 1000;
  }

  private resetNonRevertingErrors(): void {
    this.nonRevertingErrorState = null;
    this.drawingErrorChanged.emit(this.nonRevertingErrorState);
  }

  private resetWarningState(): void {
    this.warningState = null;
    this.drawingWarningChanged.emit(this.warningState);
  }

  private removeGeofenceCenterPin(): void {
    if (this.centroidForGeofence) {
      this.centroidForGeofence.setMap(null);
      this.centroidForGeofence = null;
      this.geofenceCenterChanged.emit(null);
    }
  }

  private getCenterAddressForGeofence(): google.maps.LatLng {
    switch (this.selectedTool) {
    case TOOL.POLYGON:
    case TOOL.DRAWING:
      let centroid: google.maps.LatLng = getCentroidForGeofence(this.getPolygonPathArray());
      if (!google.maps.geometry.poly.containsLocation(centroid, this.polygon)) {
        centroid = this.markers[Math.floor(Math.random() * this.markers.length)].getPosition();
      }
      return centroid;
    case TOOL.CIRCLE:
      return this.circle.getCenter();
    }
  }

  /**
   * Initialize or reposition the center icon, and emit change event, then update the zoom.
   * @param centerPoint The point at which to center the icon.
   */
  private drawAddressPinForGeofence(centerPoint: google.maps.LatLng): void {
    this.geofenceCenterChanged.emit(centerPoint);
    if (!this.centroidForGeofence) {
      this.centroidForGeofence = new google.maps.Marker({
        map: this.mapRef,
        icon: this.getCentroidMarkerIcon(ADDRESS_PIN_TYPES.DEFAULT),
        draggable: false,
        clickable: false,
        animation: google.maps.Animation.DROP,
        position: centerPoint,
        zIndex: MARKER_ICON_Z_INDEX,
      });
    } else {
      this.centroidForGeofence.setIcon(this.getCentroidMarkerIcon(ADDRESS_PIN_TYPES.DEFAULT));
      this.centroidForGeofence.setPosition(centerPoint);
    }
    this.setGeofenceInView();
  }

  private attachInsertAtListener(): void {
    this.polygonInsertAtEvent = google.maps.event.addListener(this.polygon.getPath(), POLYGON_INSERT_MARKER,
      (position: number) => {
        this.editPolygon(POLYGON_INSERT_MARKER, position);
      },
    );
  }

  private removeInsertAtListener(): void {
    google.maps.event.removeListener(this.polygonInsertAtEvent);
  }

  private resetGeofenceErrorStates(): void {
    this.resetErrorState();
    this.resetNonRevertingErrors();
  }

  private removeMapObjects(): void {
    this.markers.map((marker: google.maps.Marker) => marker.setMap(null));
    this.markers = [];
    this.markersBackup = [];
    this.polygonDragStartAt = null;
    this.updateMarkers();
    this.polygon.setMap(null);
    this.circle.setMap(null);
    this.updateGeofenceAddressPin();
    this.polygonChanged.emit(null);
  }

  /**
   * Recalculate and draw the centroid of the visible geofence, then update the zoom.
   */
  private updateGeofenceAddressPin(): void {
    if (this.lockedGeofenceAddress) {
      this.drawAddressPinForGeofence(this.lockedGeofenceAddress);
    } else if (!this.isShapeInMap) {
      this.removeGeofenceCenterPin();
    } else {
      let center: google.maps.LatLng;
      switch (this.selectedTool) {
      case TOOL.POLYGON:
      case TOOL.DRAWING:
        center = getCentroidForGeofence(this.getPolygonPathArray());
        break;
      case TOOL.CIRCLE:
        center = this.circle.getCenter();
      }
      this.drawAddressPinForGeofence(center);
    }
  }

  private drawBackupPolygon(lastGeofencePath: google.maps.LatLng[]): void {
    lastGeofencePath.forEach((point: google.maps.LatLng) => {
      this.drawMarker(point);
    });
    this.drawPolygon(false);
  }

  private updateCircle(center: google.maps.LatLng, radius: number): void {
    this.polygon.setMap(null);
    this.polylines.default.setMap(null);
    this.disableDrawing();
    this.hideCursorDot(true);
    this.mapRef.setOptions({ draggableCursor: 'pointer' });

    const editImage: HTMLElement = document.querySelector("img[src$='undo_poly.png']");
    if (editImage) editImage.style.display = 'none';

    this.skipRadiusRoundingOnce = true;
    this.circle.setOptions({ map: this.mapRef, center, radius });

    this.polygonChanged.emit(this.getGeofenceChangeData(false));

    this.updateGeofenceAddressPin();
    this.validateNonRevertingErrors();
  }

  private editPolygon(type: string, position?: number): void {
    const editImage: HTMLElement = document.querySelector("img[src$='undo_poly.png']");
    if (editImage) editImage.style.display = 'none';
    if (type === POLYGON_INSERT_MARKER) {
      const mouseClickedEvent: google.maps.MapMouseEvent = this.mouseClickedEvent;
      const insertedPoint: google.maps.LatLng = this.polygon.getPath().getAt(position);
      const marker: google.maps.Marker = this.draggedMarker = new this.gmaps.Marker({
        position: insertedPoint,
        icon: this.markerIcons.large,
        draggable: true,
        map: this.mapRef,
        clickable: true,
        cursor: 'pointer',
        zIndex: MARKER_ICON_Z_INDEX,
      });

      this.markers.splice(position, 0, marker);
      this.registerMarkerEvents(this.markers[position]);
      mouseClickedEvent.latLng = insertedPoint;
      this.onMarkerDragEnd(mouseClickedEvent, this.markers[position]);
    }
  }

  private destroyPolygonFence(): void {
    this.polygon.setOptions({
      map: null,
      paths: [],
    });
    if (this.centroidForGeofence) this.centroidForGeofence.setMap(null);
    this.markers.map((marker: google.maps.Marker) => marker.setMap(null));
    this.markers = [];
    this.markersBackup = [];
  }

  private removeLastMarker(saveLast: boolean): void {
    this.markers[this.markers.length - 1].setMap(null);
    if (saveLast) this.markersBackup.push(this.markers.pop());
    else this.markers.pop();
    this.updateMarkers();
  }

  private addLastMarker(): void {
    const lastMarker = this.markersBackup.pop();
    lastMarker.setMap(this.mapRef);
    this.markers.push(lastMarker);
    this.updateMarkers();
  }

  private extendPolyLineToPoint(points: google.maps.LatLng[]): void {
    const options: google.maps.PolylineOptions = {
      path: points,
      map: this.mapRef,
    };
    this.polylines.default.setOptions(options);
  }

  private disableDrawing(): void {
    this.isDrawingModeEnabled = false;
    this.gmaps.event.removeListener(this.mapClickedEvent);
    this.mapClickedEvent = null;
  }

  private setPolygonIcons(): void {
    this.markers.forEach(marker => {
      marker.setOptions({
        position: marker.getPosition(),
        icon: this.markerIcons.completed,
        opacity: this.isPolygonInMap ? 0 : 1,
        cursor: 'pointer',
        draggable: true,
        zIndex: GEOFENCE_MARKER_Z_INDEX,
      });
    });
  }

  private updateMarkers(): void {
    const points = this.markers.map((marker, index) => {
      if (index == this.markers.length - 1) marker.setIcon(this.markerIcons.large);
      else marker.setOptions(this.markerIcons.default);
      marker.setOpacity(this.isPolygonInMap ? 0 : 1);
      marker.setDraggable(true);
      if (index == 0) {
        marker.setDraggable(false);
        marker.setCursor('crosshair');
      }
      return marker.getPosition();
    });
    this.extendPolyLineToPoint(points);
  }

  private getPolygonPathArray(): google.maps.LatLng[] {
    switch (this.selectedTool) {
    case TOOL.POLYGON:
    case TOOL.DRAWING:
      return this.polygon.getPath().getArray();
    case TOOL.CIRCLE:
      return convertCircleToPath(this.circle);
    }
  }

  private updatePolygon(scrollView?: boolean): void {
    this.disableDrawing();
    this.polylines.default.setMap(null);
    this.setPolygonIcons();
    this.hideCursorDot(true);
    this.mapRef.setOptions({
      draggableCursor: 'pointer',
    });
    this.markersBackup = [];

    this.polygonChanged.emit(this.getGeofenceChangeData(scrollView));

    if (this.isDrawingThroughFV) {
      if (!this.lockedGeofenceAddress) this.updateGeofenceAddressPin();
      else this.setGeofenceInView();
    }
    this.validateNonRevertingErrors();
    this.attachInsertAtListener();
  }

  private errorStateChanged(): void {
    if (this.revertingErrorState) {
      this.resetWarningState();
      this.disableDrawing();
      this.showGeofenceError();
      this.drawingErrorChanged.emit(this.revertingErrorState);
    } else {
      this.enableDrawing();
    }
  }

  private showGeofenceError(): void {
    let polylineErrorPath: google.maps.LatLng[] = [];
    this.enableDrawingControls = false;
    this.hideCursorDot(true);
    if (this.draggedMarker) {
      const markerIndex: number = this.markers.findIndex(marker => marker == this.draggedMarker);
      const previousPoint: google.maps.LatLng = markerIndex > 0 ?
        this.markers[markerIndex - 1].getPosition() :
        this.markers[this.markers.length - 1].getPosition()
      ;
      const nextPoint: google.maps.LatLng = markerIndex != this.markers.length - 1 ?
        this.markers[markerIndex + 1].getPosition() :
        (this.isPolygonInMap ? this.markers[0].getPosition() : null)
      ;

      if (previousPoint) polylineErrorPath.push(previousPoint);
      polylineErrorPath.push(this.draggedMarker.getPosition());
      if (nextPoint) polylineErrorPath.push(nextPoint);

      this.draggedMarker.setIcon(this.markerIcons.error);
    } else if (this.polygonDragStartAt) {
      this.markers.forEach((marker: google.maps.Marker) => marker.setIcon(this.markerIcons.error));
      polylineErrorPath = this.getPolygonPathArray();
      polylineErrorPath.push(polylineErrorPath[0]); // add first point at the end to complete the path
    } else {
      if (this.markers?.length > 0) polylineErrorPath = [this.markers[this.markers.length - 1].getPosition(), this.clickedPoint];
    }

    if (polylineErrorPath.length > 0) {
      this.polylines.error.setOptions({
        path: polylineErrorPath,
        icons: [{
          icon: !this.draggedMarker ? this.markerIcons.error : null,
          offset: '100%',
        }],
      });
      this.setShapeErrorState();
      this.polylines.errorBorder.setPath(polylineErrorPath);
    } else if (!this.polygonDragStartAt) this.drawErrorMarker(this.clickedPoint);

    setTimeout(() => {
      this.revertErrorChanges();
      this.resetErrorState();
    }, 3000);
  }

  private resetErrorState(): void {
    if (this.revertingErrorState) {
      this.revertingErrorState = null;
      if (this.errorMarker) {
        this.errorMarker.setMap(null);
        this.errorMarker = null;
        return;
      }

      this.polylines.errorBorder.setPath([]);
      this.polylines.error.setPath([]);
      this.draggedMarker = null;
      this.markerDragStartAt = null;
      this.polygonDragStartAt = null;
      if (!this.isPolygonInMap) {
        this.drawingErrorChanged.emit(null);
      }
    }
  }

  private drawErrorMarker(point: google.maps.LatLng): void {
    if (this.errorMarker) this.errorMarker.setMap(null);
    this.errorMarker = new this.gmaps.Marker({
      position: point,
      icon: this.markerIcons.error,
      draggable: false,
      map: this.mapRef,
      clickable: false,
      cursor: 'pointer',
      zIndex: MARKER_ICON_Z_INDEX,
    });
  }

  private revertErrorChanges(): void {
    if (this.revertingErrorState) {
      this.enableDrawingControls = true;
      if (this.markers?.length > 0 && !this.markers[0].getMap()) this.markers[0].setMap(this.mapRef);
      if (this.draggedMarker) {
        this.draggedMarker.setPosition(this.markerDragStartAt);
        this.updateMarkers();
      }
      if (this.isPolygonInMap) {
        this.drawPolygon(false);
      } else {
        this.enableDrawing();
        this.setDrawingTheme();
      }
    }
  }

  private registerMapEvents(): void {
    this.mapDragStartEvent = this.mapRef.addListener('dragstart', () => {
      this.hideCursorDot(true);
    });
    this.mapDragEndEvent = this.mapRef.addListener('dragend', () => {
      if (this.isDrawingModeEnabled) {
        this.hideCursorDot(false);
      }
    });
    if (!this.mapClickedEvent) {
      this.mapClickedEvent = this.mapRef.addListener('click', this.onMapClicked.bind(this));
      this.mapRef.addListener('mousemove', this.onMapMouseMove.bind(this));
    }
  }

  private onMapClicked(event: google.maps.MapMouseEvent): void {
    this.startDrawing();
    if (this.isDrawingModeEnabled) {
      this.clickedPoint = event.latLng;
      // validate point before drawing marker ;
      this.revertingErrorState = this.revertingErrorStates[
        this.validateLineOverlapError(event) ||
        this.validateNumberOfPoints() ||
        this.validateMarkerToLineOverlap(event.latLng)
      ];
      this.errorStateChanged();
      if (!this.revertingErrorState) this.drawMarker(event.latLng);
    } else if (!this.isPolygonInMap) this.enableDrawing();
  }

  private validateSquareFenceMinimumArea(): keyof IGeofenceWarningState {
    const areaOfpolygon: number = google.maps.geometry.spherical.computeArea(this.getMarkerPositions());
    if (areaOfpolygon <= WARNING_THRESHOLD_FENCE_AREA_SQ_METERS &&
      areaOfpolygon > MINIMUM_FENCE_AREA_SQ_METERS ) {
      return 'small';
    } else {
      return null;
    }
  }

  private validateNumberOfPoints(): string {
    if (this.markers?.length < MAX_GEOFENCE_POINTS) return '';
    else return 'tooManyPoints';
  }

  private validateLineOverlapError(event: google.maps.MapMouseEvent): string {
    if (findOverlappingLines(event.latLng, this.markers, false, this.isPolygonInMap)) return 'overlapped';
    else return '';
  }

  private validateMarkerOverlapError(draggedMarker: google.maps.Marker): string {
    return findOverlappingMarkers(
      draggedMarker,
      this.markers,
      draggedMarker == this.markers[this.markers.length - 1],
    ) ? 'overlapped' : '';
  }

  private validateMinimumGeofenceSize(): keyof IGeofenceNonRevertingErrorStates {
    return !validateMinimumGeofenceSize(this.markers) ? 'tooSmall' : null;
  }

  private validateMaximumGeofenceSize(): keyof IGeofenceNonRevertingErrorStates {
    return !validateMaximumGeofenceSize(this.markers) ? 'tooLarge' : null;
  }

  private validateCircleMinimumRadius(): keyof IGeofenceNonRevertingErrorStates {
    if (this.isMetricUnitsSelected) {
      return this.circle.getRadius() <= MINIMUM_CIRCLE_RADIUS_METERS ? 'radiusTooSmall' : null;
    } else {
      return this.convertRadiusMetersToMilesOrKm(this.circle.getRadius()) <= MINIMUM_CIRCLE_RADIUS_MILE ? 'radiusTooSmall' : null;
    }
  }

  private validateCircleMaxiumRadius(): keyof IGeofenceNonRevertingErrorStates {
    if (this.isMetricUnitsSelected) {
      return this.circle.getRadius() >= MAXIMUM_CIRCLE_RADIUS_METERS ? 'radiusTooLarge' : null;
    } else {
      return this.convertRadiusMetersToMilesOrKm(this.circle.getRadius()) >= MAXIMUM_CIRCLE_RADIUS_MILE ? 'radiusTooLarge' : null;
    }
  }

  private validateMarkerToLineOverlap(point: google.maps.LatLng): string {
    if (this.markers?.length >= 3) {
      const points: google.maps.LatLng[] = this.markers.map((marker: google.maps.Marker) => marker.getPosition());
      return findMarkerPolylineOverlapping(point, points, this.mapRef) ? 'overlapped' : '';
    } else return '';
  }

  private validateAddressForGeofence(): string {
    const addressLatLng: google.maps.LatLng = this.lockedGeofenceAddress ?
      this.lockedGeofenceAddress : this.getCenterAddressForGeofence();
    switch (this.selectedTool) {
    case TOOL.POLYGON:
    case TOOL.DRAWING:
      return !validateAddressInsideGeofence(addressLatLng, this.polygon) ? GEOFENCE_ADDRESS_ERROR_STATE : null;
    case TOOL.CIRCLE:
      const isInGeofence: boolean = getDistance(addressLatLng, this.circle.getCenter()) <= this.circle.getRadius();
      return !isInGeofence ? GEOFENCE_ADDRESS_ERROR_STATE : null;
    }
  }

  private drawMarker(points: google.maps.LatLng): void {
    const marker = new this.gmaps.Marker({
      position: points,
      icon: this.markerIcons.large,
      draggable: true,
      map: this.mapRef,
      clickable: true,
      cursor: 'pointer',
      zIndex: MARKER_ICON_Z_INDEX,
    });
    this.registerMarkerEvents(marker);
    this.markers.push(marker);
    this.markersBackup = [];
    this.isPolygonDestroyed = false;
    this.updateMarkers();

  }

  private registerMarkerEvents(marker: google.maps.Marker): void {
    marker.addListener('mousedown', event => this.markerDragStartAt = marker.getPosition());
    marker.addListener('drag', event => this.onMarkerDrag(marker));
    marker.addListener('dragend', event => this.onMarkerDragEnd(event, marker));

    if (this.markers?.length === 0) {
      this.enableDrawingControls = true;
      marker.addListener('click', this.firstMarkerClicked.bind(this));
      marker.setDraggable(false);
      marker.setCursor('crosshair');
    }

    marker.addListener('mouseover', () => this.onMarkerMouseOver(marker));
    marker.addListener('mouseout', () => this.onMarkerMouseOut(marker));
  }

  private onMarkerMouseOver(marker: google.maps.Marker): void {
    if (!this.isPolygonInMap && !this.revertingErrorState) {
      marker.setIcon(this.markerIcons.large);
      if (marker !== this.markers[0]) marker.setCursor('grab');
      else {
        this.hideCursorDot(true);
        marker.setCursor('default');
      }
    }
    if (this.isPolygonInMap) {
      marker.setCursor('grab');
    }
  }

  private onMarkerMouseOut(marker: google.maps.Marker): void {
    if (!this.isPolygonInMap && !this.revertingErrorState) {
      if (marker != this.markers[this.markers.length - 1]) marker.setIcon(this.markerIcons.default);
      this.hideCursorDot(false);
    }
  }

  private firstMarkerClicked(event: google.maps.MapMouseEvent, shouldViewscroll: boolean = true): void {
    if (this.markers?.length >= 3) {
      this.clickedPoint = event ? event.latLng : this.markers[0].getPosition();
      this.revertingErrorState = this.revertingErrorStates[
        this.validateLineOverlapError(event) ||
        this.validateMarkerToLineOverlap(event.latLng)
      ];
      if (!this.revertingErrorState) {
        this.drawPolygon(shouldViewscroll);
      }
    }
  }

  private setShapeErrorState(): void {
    if (this.polygon) this.polygon.setOptions(GEOFENCE_STYLE_OPTIONS[this.selectedMapTypeTheme].error);
    if (this.circle) this.circle.setOptions(GEOFENCE_STYLE_OPTIONS[this.selectedMapTypeTheme].error);
    if (this.centroidForGeofence) {
      this.centroidForGeofence.setIcon(this.getCentroidMarkerIcon(ADDRESS_PIN_TYPES.ERROR));
    }
  }

  private validateNonRevertingErrors(): void {
    switch (this.selectedTool) {
    case TOOL.POLYGON:
    case TOOL.DRAWING:
      this.warningState = this.warningStates[
        this.validateSquareFenceMinimumArea()
      ];
      this.nonRevertingErrorState = this.nonRevertingErrorStates[
        this.validateMaximumGeofenceSize() ||
          this.validateMinimumGeofenceSize() ||
          this.validateAddressForGeofence()
      ];
      break;
    case TOOL.CIRCLE:
      this.nonRevertingErrorState = this.nonRevertingErrorStates[
        this.validateCircleMinimumRadius() ||
          this.validateCircleMaxiumRadius() ||
          this.validateAddressForGeofence()
      ];
    }
    this.onGeofenceSizeErrorChange();
  }

  private onGeofenceSizeErrorChange(): void {
    this.setDrawingTheme();
    this.drawingWarningChanged.emit(this.warningState);
    this.drawingErrorChanged.emit(this.nonRevertingErrorState);
  }

  private drawPolygon(shouldViewscroll: boolean, path?: google.maps.LatLng[]): void {
    const points = path || this.markers.map(marker => marker.getPosition());
    this.polygon.setOptions({
      paths: points,
      map: this.mapRef,
    });
    this.updatePolygon(shouldViewscroll);
  }

  private hideCursorDot(hide: boolean): void {
    if (hide) this.cursorDot.style.visibility = 'hidden';
    else this.cursorDot.style.visibility = 'visible';
  }

  /**
   * Make the center dot of the circle disappear so that the user can't drag it.
   */
  private hideCircleCenter(): void {
    window.setTimeout(() => {
      const circleCenterMarker: HTMLElement = document.querySelector("div[style='position: absolute; left: 0px; top: 0px; z-index: 3;']>div");
      circleCenterMarker.style.display = 'none !important';
      circleCenterMarker.style.visibility = 'hidden';
    }, 0);
  }

  private onMarkerDrag(marker?: google.maps.Marker): void {
    this.removeInsertAtListener();
    if (marker) marker.setCursor('grabbing');
    this.hideCursorDot(true);
    const markerPoints = this.markers.map(m => m.getPosition());
    this.polylines.default.setPath(markerPoints);
    if (this.isPolygonInMap) this.polygon.setPath(markerPoints);
    this.attachInsertAtListener();
  }

  private onMarkerDragEnd(event: google.maps.MapMouseEvent, marker: google.maps.Marker): void {
    this.draggedMarker = marker;
    this.revertingErrorState = this.revertingErrorStates[
      this.validateLineOverlapError(event) ||
      this.validateMarkerOverlapError(marker) ||
      this.validateMarkerToLineOverlap(event.latLng)
    ];

    if (this.revertingErrorState) this.errorStateChanged();
    else {
      this.draggedMarker = null;
      this.markerDragStartAt = null;
      if (this.isPolygonInMap) {
        this.removeInsertAtListener();
        this.updatePolygon(false);
        this.updatePolygonEdits();
      } else {
        this.hideCursorDot(false);
        if (this.isAttemptToClosePath(marker)) {
          this.removeLastMarker(false);
          this.clickedPoint = event ? event.latLng : this.markers[0].getPosition();
          this.drawPolygon(true);
        }
      }
    }
  }

  private updatePolygonEdits(): void {
    if (this.selectedTool === TOOL.POLYGON) {
      this.polygonGeofenceEdits.push(this.getMarkerPositions());
    }
  }

  private getMarkerPositions(): google.maps.LatLng[] {
    let markersPositions: google.maps.LatLng[] = [];
    markersPositions = this.markers.map(marker => marker.getPosition());
    return markersPositions;
  }

  private isAttemptToClosePath(marker: google.maps.Marker): boolean {
    return this.markers?.length > 3 &&
      this.markers[this.markers.length - 1] === marker &&
      getDistance(marker.getPosition(), this.markers[0].getPosition()) < MINIMUM_MARKER_DISTANCE
    ;
  }

  private onMapMouseMove(event: any): void {
    this.cursorDot.style.left = `${event.pixel.x - 8}px`;
    this.cursorDot.style.top = `${event.pixel.y - 12}px`;
  }

  private initPolylines(): void {
    polylinesPromise(this.mapRef).then(polylines => {
      this.polylines = polylines;
      this.polylines.default.setOptions(POLYLINE_STYLE_OPTIONS[this.selectedMapTypeTheme]);
    });
  }

  private initMarkerIcons(): void {
    markerIconsPromise.then(icons => {
      this.selectedMapThemeDrawingMarkers = icons;
      this.markerIcons = this.selectedMapThemeDrawingMarkers[this.selectedMapTypeTheme];
    });
    if (this.areNewMarkersEnabled) {
      newCentroidMarkerIconsPromise.then(CENTROID_THEME_ICONS => {
        this.newCentroidMarkerIcons = CENTROID_THEME_ICONS;
        if (this.map) this.updateGeofenceAddressPin();
      });
    } else {
      centroidMarkerIconsPromise.then(CENTROID_THEME_ICONS => {
        this.centroidMarkerIcons = CENTROID_THEME_ICONS;
        if (this.map) this.updateGeofenceAddressPin();
      });
    }
  }

  private initPolygon(): void {
    polygonPromise(this.mapRef).then(polygon => {
      this.polygon = polygon;
      this.polygon.addListener('drag', this.onPolygonDrag.bind(this));
      this.polygon.addListener('dragstart', this.onPolygonDragStart.bind(this));
      this.polygon.addListener('dragend', this.onPolygonDragEnd.bind(this));
      this.polygon.addListener('mouseover', this.onShapeMouseOver.bind(this, this.polygon));
      this.polygon.addListener('mouseout', this.onShapeMouseOut.bind(this, this.polygon));
      this.polygon.addListener('mousedown', (e: google.maps.MapMouseEvent) => {
        this.markers.map((marker: google.maps.Marker) => marker.setMap(null));
        this.markerDragStartAt = e.latLng;
      });
      this.polygon.addListener('mouseup', () => {
        this.markers.map((marker: google.maps.Marker) => marker.setMap(this.mapRef));
      });
      this.setDrawingTheme();
    });
  }

  private initCircle(): void {
    circlePromise().then(circle => {
      this.circle = circle;
      this.circle.addListener('radius_changed', this.onCircleChange.bind(this));
      this.circle.addListener('dragend', this.onCircleChange.bind(this));
      this.circle.addListener('mouseover', this.onShapeMouseOver.bind(this, this.circle));
      this.circle.addListener('mouseout', this.onShapeMouseOut.bind(this, this.circle));
      this.circle.addListener('mousedown', (e: google.maps.MapMouseEvent) => {
        this.markers.map((marker: google.maps.Marker) => marker.setMap(null));
        this.markerDragStartAt = e.latLng;
      });
      this.circle.addListener('mouseup', () => {
        this.markers.map((marker: google.maps.Marker) => marker.setMap(this.mapRef));
      });
      this.setDrawingTheme();
    });
  }

  private setShapeDefaultState(): void {
    if (this.polygon) this.polygon.setOptions(GEOFENCE_STYLE_OPTIONS[this.selectedMapTypeTheme].default);
    if (this.circle) this.circle.setOptions(GEOFENCE_STYLE_OPTIONS[this.selectedMapTypeTheme].default);
    if (this.centroidForGeofence) {
      this.centroidForGeofence.setIcon(this.getCentroidMarkerIcon(ADDRESS_PIN_TYPES.DEFAULT));
    }
  }

  private getCentroidMarkerIcon(iconType: ADDRESS_PIN_TYPES): string {
    const pinType: string = this.lockedGeofenceAddress ? 'address_added' : 'address_error';
    if (this.areNewMarkersEnabled) {
      return this.newCentroidMarkerIcons[pinType][iconType].icon;
    }
    return this.centroidMarkerIcons[pinType][this.selectedMapTypeTheme][iconType].icon;
  }

  private setDrawingTheme(): void {
    if (this.nonRevertingErrorState) this.setShapeErrorState();
    else this.setShapeDefaultState();
    this.markerIcons = this.selectedMapThemeDrawingMarkers[this.selectedMapTypeTheme];
    this.polylines.default.setOptions(POLYLINE_STYLE_OPTIONS[this.selectedMapTypeTheme]);
    this.setPolygonIcons();
  }

  private onShapeMouseOver(shape: GoogleShape): void {
    shape.setOptions({
      fillOpacity: GEOFENCE_HOVER_OPACITY,
    });
    if (!this.isDrawingThroughFV) {
      this.map.el.querySelector('#map').classList.add('dragging');
    }
  }

  private onShapeMouseOut(shape: GoogleShape): void {
    shape.setOptions({
      fillOpacity: GEOFENCE_CLASSIC_THEME_OPACITY,
    });
    if (!this.isDrawingThroughFV) {
      this.map.el.querySelector('#map').classList.remove('dragging');
    }
  }

  private onPolygonDrag(): void {
    this.getPolygonPathArray().forEach((latLng: google.maps.LatLng, index: number) => {
      if (this.markers[index]) this.markers[index].setPosition(latLng);
    });
  }

  private onCircleChange(): void {
    this.removeInsertAtListener();

    this.hideCircleCenter();

    // Fix radius to a whole number if it is above 1.
    const radius: number = this.circle.getRadius();
    if (!this.skipRadiusRoundingOnce) {
      const roundRadius: number = this.conditionalRoundRadiusMeters(radius);
      if (roundRadius !== radius) {
        this.circle.setRadius(roundRadius);
        return;
      }
    }

    // Save the change if it was not triggered by an undo.
    const center: google.maps.LatLng = this.circle.getCenter();
    const { radius: prevRadius, center: prevCenter } =
      this.circleEdits.length ? this.circleEdits[this.circleEdits.length - 1] : this.defaultCircleEditData;
    const isUndo: boolean = radius === prevRadius && center.lat() === prevCenter?.lat() && center.lng() === prevCenter?.lng();
    if (center && radius && !isUndo) {
      this.circleEdits.push({ center: new google.maps.LatLng(center.lat(), center.lng()), radius: this.circle.getRadius() });
    }

    const convertedRadius: number = this.convertRadiusMetersToMilesOrKm(radius);
    if (convertedRadius !== parseFloat(this.formRadius)) {
      this.formRadius = convertedRadius.toString();
    }

    this.polygonChanged.emit(this.getGeofenceChangeData(false));

    this.updateGeofenceAddressPin();

    this.validateNonRevertingErrors();
    this.attachInsertAtListener();

    this.skipRadiusRoundingOnce = false;
  }

  private getBounds(): google.maps.LatLngBounds {
    switch (this.selectedTool) {
    case TOOL.POLYGON:
    case TOOL.DRAWING:
      const latlngbounds: google.maps.LatLngBounds = new google.maps.LatLngBounds();
      const markersOnTheMap: google.maps.Marker[] = cloneDeep(this.markers);
      if (this.centroidForGeofence) markersOnTheMap.push(this.centroidForGeofence);
      markersOnTheMap.forEach((marker: google.maps.Marker) => latlngbounds.extend(marker.getPosition()));
      return latlngbounds;
    case TOOL.CIRCLE:
      return this.circle.getBounds();
    }
  }

  private getGeofenceChangeData(scrollView?: boolean): IGeofenceChangeData {
    const geofenceChangeData: IGeofenceChangeData = {
      polygonPath: this.getPolygonPathArray(),
      scrollView: false,
    };
    if (this.selectedTool === TOOL.CIRCLE) {
      geofenceChangeData.center = this.circle.getCenter();
      geofenceChangeData.radius = this.circle.getRadius();
    }
    return geofenceChangeData;
  }

  /**
   * Zoom the canvas in or out as appropriate to center the geofence at a
   * distance that is pleasant to look at.
   */
  private setGeofenceInView(): void {
    if (this.centroidForGeofence) {
      this.mapRef.fitBounds(this.getBounds());
      this.mapRef.setZoom(this.mapRef.getZoom() - GEOFENCE_SELECTION_ZOOM_OUT_OFFSET);
    }
  }

  private onPolygonDragStart(): void {
    this.polygonDragStartAt = cloneDeep(this.getPolygonPathArray());
  }

  private onPolygonDragEnd(): void {
    this.removeInsertAtListener();
    const markerPoints = this.markers.map(m => m.getPosition());
    this.polylines.default.setPath(markerPoints);
    if (this.isPolygonInMap) this.polygon.setPath(markerPoints);
    this.polygonChanged.emit(this.getGeofenceChangeData(false));
    if (this.isDrawingThroughFV) {
      if (!this.lockedGeofenceAddress) this.updateGeofenceAddressPin();
      else this.setGeofenceInView();
    }
    this.validateNonRevertingErrors();
    if (this.selectedTool === TOOL.POLYGON) this.polygonGeofenceEdits.push(this.getMarkerPositions());
    this.attachInsertAtListener();
  }

  private initializeErrorAndWarningMessages(): void {
    const meterSqMetricUnit: string = this.metricUnitsPipe.transform(this.isMetricUnitsSelected, 0, 'distanceSqM');
    const kmSqMetricUnit: string = this.metricUnitsPipe.transform(this.isMetricUnitsSelected, 0, 'distanceSqKm');
    const kmMetricUnit: string = this.metricUnitsPipe.transform(this.isMetricUnitsSelected, 0, 'distance');

    const minFenceArea: string = this.isMetricUnitsSelected ?
      `${formatNumber(MINIMUM_FENCE_AREA_SQ_METERS)} ${meterSqMetricUnit}` :
        `${formatNumber(MINIMUM_FENCE_AREA_SQ_FEET)} ${meterSqMetricUnit}`
    ;
    const maxFenceArea: string = this.isMetricUnitsSelected ?
      `${formatNumber(MAXIMUM_FENCE_AREA_SQ_KM)} ${kmSqMetricUnit}` : `${formatNumber(MAXIMUM_FENCE_AREA_SQ_MILE)} ${kmSqMetricUnit}`
    ;
    const warningThresholdArea: string = this.isMetricUnitsSelected ?
      `${formatNumber(WARNING_THRESHOLD_FENCE_AREA_SQ_METERS)} ${meterSqMetricUnit}` :
        `${formatNumber(WARNING_THRESHOLD_FENCE_AREA_SQ_FEET)} ${meterSqMetricUnit}`
    ;
    const minRadius: string = this.isMetricUnitsSelected ?
      `${formatNumber(MINIMUM_CIRCLE_RADIUS_METERS / 1000)} ${kmMetricUnit}` : `${formatNumber(MINIMUM_CIRCLE_RADIUS_MILE)} ${kmMetricUnit}`;
    const maxRadius: string = this.isMetricUnitsSelected ?
      `${formatNumber(MAXIMUM_CIRCLE_RADIUS_METERS / 1000)} ${kmMetricUnit}` : `${formatNumber(MAXIMUM_CIRCLE_RADIUS_MILE)} ${kmMetricUnit}`;

    this.revertingErrorStates.overlapped.message = $localize`Paths are not allowed to cross each other.`;
    this.nonRevertingErrorStates.tooSmall.message = $localize`This geofence is too small. Please adjust the size so that its at least ${minFenceArea} for reliable monitoring.`;
    this.nonRevertingErrorStates.tooLarge.message = $localize`This geofence is too big. Please adjust the size so that its smaller than ${maxFenceArea}.`;
    this.nonRevertingErrorStates.radiusTooSmall.message = $localize`This geofence is too small. Please adjust the size so that it has a radius of at least ${minRadius} for reliable monitoring.`;
    this.nonRevertingErrorStates.radiusTooLarge.message = $localize`This geofence is too big. Please adjust the size so that it\'s smaller than ${maxRadius} in radius.`;
    this.revertingErrorStates.tooManyPoints.message = $localize`Too many points (maximum of ${MAX_GEOFENCE_POINTS}). Reduce the number of points.`;
    this.warningStates.small.message = $localize
    `Are you creating this geofence to track assets using the Asset Gateway? Based on typical GPS accuracy, we recommend making it at least ${warningThresholdArea} for reliable monitoring.`;
  }

  private initMapFunctionsAndEvents(): void {
    this.mapRef.setOptions({
      draggableCursor: 'pointer',
      clickableIcons: false,
      mapTypeId: this.selectedMapType ? this.selectedMapType : 'hybrid',
    });
    this.initPolylines();
    this.initMarkerIcons();
    this.initPolygon();
    this.initCircle();
    this.registerMapEvents();
  }

  private getFormRadiusInputRegExp(): RegExp {
    const groups: string[] = [];
    for (let i = 1; i < MAX_FORM_RADIUS_CHAR; i++) {
      groups.push(`(\\d{0,${i}}\\.?\\d{0,${MAX_FORM_RADIUS_CHAR - i}})`);
    }
    return new RegExp(`^(${groups.join('|')})$`);
  }
}
